// src/components/checkout/CheckoutSummary.jsx
import { useNavigate } from "react-router-dom";

import { FlipAmount, ProductImage } from "common";
import { formatCurrency, getCurrentPrice } from "config/config";

import { Icon } from "@/design-system";

/**
 * The reference checkout's sticky "Your order" rail (grep `===== CHECKOUT` in
 * `Dens N Dente Tailwind.dc.html`): item count + "View full cart", a scrollable
 * mini-line list (38px thumb, truncated name, promo/auto-delivery pills, struck
 * was-price), then Subtotal / Promo savings / Delivery / Tax / Total.
 *
 * Two adaptations from the reference:
 * - **Lines are read-only** — no stepper/delete here. Checkout edits go through
 *   "View full cart"; editing quantities mid-checkout would silently detach the
 *   `selectedIds` the flow was entered with.
 * - **Delivery and Tax are real, not demo numbers**: they arrive as props from
 *   `CheckoutPage`'s address-driven `handleTaxShippingEstimate`, and read
 *   "Calculated from your address" until an address makes them known. `Total`
 *   is `calculateOrderTotal`'s output (which owns the free-shipping rule).
 */
export default function CheckoutSummary({
  shippingCost = null,
  estimatedTax = null,
  taxRate = null,
  calculatedTotal = null,
  subtotal,
  cart,
}) {
  const navigate = useNavigate();

  const lines = cart.map((item) => {
    const { basePrice, currentPrice, savePrice } = getCurrentPrice(
      item,
      item.quantity
    );
    return {
      item,
      total: currentPrice * item.quantity,
      was: savePrice > 0 ? basePrice * item.quantity : null,
      save: savePrice * item.quantity,
    };
  });
  const savings = lines.reduce((sum, line) => sum + (line.save || 0), 0);
  const itemCount = cart.reduce(
    (sum, item) => sum + (Number(item.quantity) || 1),
    0
  );

  const hasShip = shippingCost !== null;
  const hasTax = estimatedTax !== null;

  return (
    <div className="sticky top-[200px] flex flex-col gap-[14px] rounded-xl border border-line-subtle bg-card px-[var(--space-6)] py-[var(--space-6)] shadow-sm">
      <div className="text-md font-bold">Your order</div>

      <div className="flex items-center justify-between">
        <div className="text-[13px] text-ink-2">
          {itemCount} item{itemCount !== 1 ? "s" : ""}
        </div>
        <button
          onClick={() => navigate("/cart")}
          className="cursor-pointer border-0 bg-transparent px-[var(--space-1)] py-[2px] font-sans text-xs font-semibold text-ink-link"
        >
          View full cart
        </button>
      </div>

      <div className="dnd-thinscroll flex max-h-[320px] flex-col gap-[14px] overflow-y-auto">
        {lines.map(({ item, total, was, save }) => (
          <div key={item.id} className="flex items-start gap-[10px]">
            <span className="inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center overflow-hidden rounded-sm bg-[var(--neutral-50)]">
              <ProductImage
                src={item.file_url}
                alt={item.itemid}
                className="h-full w-full object-cover"
              />
            </span>
            <div className="min-w-0 flex-1">
              <div className="overflow-hidden text-ellipsis whitespace-nowrap text-xs font-semibold">
                {item.itemid}
              </div>
              <div className="mt-[2px] text-2xs text-ink-3">
                Qty {item.quantity}
              </div>
              {was !== null && (
                <span className="mt-[3px] inline-flex items-center gap-[5px] rounded-pill bg-success-50 py-[2px] pl-[6px] pr-[9px] text-2xs font-semibold text-success-700">
                  <Icon name="sell" size={13} fill />
                  You save {formatCurrency(save)}
                </span>
              )}
              {!was && item.custrecord_ci_sub_enabled && (
                <span className="mt-[3px] inline-flex items-center gap-[5px] rounded-pill bg-soft py-[2px] pl-[6px] pr-[9px] text-2xs font-semibold text-blue-700">
                  <Icon name="redeem" size={13} />
                  On auto-delivery
                </span>
              )}
            </div>
            <span className="flex shrink-0 flex-col items-end">
              <span className="text-sm font-bold">{formatCurrency(total)}</span>
              {was !== null && (
                <span className="text-2xs text-ink-3 line-through">
                  {formatCurrency(was)}
                </span>
              )}
            </span>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-[10px] border-t border-line-subtle pt-[14px]">
        <div className="flex justify-between text-sm">
          <span className="text-ink-2">Subtotal</span>
          <span>{formatCurrency(subtotal)}</span>
        </div>
        {savings > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-ink-2">Promo savings</span>
            <span className="font-semibold text-success-600">
              −{formatCurrency(savings)}
            </span>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span className="text-ink-2">Delivery</span>
          <span>
            {hasShip
              ? shippingCost === 0
                ? "Free"
                : formatCurrency(shippingCost)
              : "Calculated from your address"}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-ink-2">
            {/* `taxRate` is a fraction (0.13, as GET_TAX_RATES returns it),
                so it's scaled for display; parseFloat drops "13.00" to "13"
                while keeping "14.975" → "14.98" for GST+QST. */}
            {hasTax && taxRate
              ? `Tax (${parseFloat((taxRate * 100).toFixed(2))}%)`
              : "Tax"}
          </span>
          <span>{hasTax ? formatCurrency(estimatedTax) : "—"}</span>
        </div>
        <div className="flex justify-between border-t border-line-subtle pt-[10px] text-lg font-bold">
          <span>Total</span>
          <FlipAmount
            text={formatCurrency(calculatedTotal ?? subtotal)}
            className="text-lg font-bold"
          />
        </div>
      </div>

      <div className="flex items-center gap-[var(--space-2)] text-xs text-ink-2">
        <Icon name="verified" size={16} className="text-blue-600" />
        We review every order and flag anything off.
      </div>
    </div>
  );
}
